import { useEffect, useRef } from 'react';

/** sessionStorage key used by the command bar to relay a freeform prompt. */
export const COPILOT_PREFILL_KEY = 'sz:copilot-prefill';
/** localStorage key tracking the last copilot thread the user visited. */
export const LAST_COPILOT_THREAD_KEY = 'sz:last-copilot-thread';

/**
 * Glue between the command bar and the copilot page.
 *
 * - Reads a one-shot prompt from `sessionStorage` (set by the command bar's
 *   "Ask Copilot" action) and dispatches it via the provided send callback.
 * - Persists the active thread id to `localStorage` so "Resume last chat" can
 *   navigate back to it from anywhere in the app.
 *
 * @param activeThreadId - Currently loaded copilot thread id, or null
 * @param sendMessage - Callback that submits a message into the active chat
 */
export function useCopilotPrefillAndTracker(
  activeThreadId: string | null | undefined,
  sendMessage: (message: string) => void,
) {
  const consumedPrefillRef = useRef(false);

  useEffect(() => {
    if (consumedPrefillRef.current) return;
    if (typeof window === 'undefined') return;

    const prefill = window.sessionStorage.getItem(COPILOT_PREFILL_KEY);
    if (prefill && prefill.trim().length > 0) {
      consumedPrefillRef.current = true;
      window.sessionStorage.removeItem(COPILOT_PREFILL_KEY);
      sendMessage(prefill);
    }
  }, [sendMessage]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (activeThreadId) {
      try {
        window.localStorage.setItem(LAST_COPILOT_THREAD_KEY, activeThreadId);
      } catch {
        // localStorage unavailable — ignore.
      }
    }
  }, [activeThreadId]);
}
